import { PuzzleValidator } from './puzzle-validator.js';
import { instrumentation } from './instrumentation.js';

export class PuzzleLoader {
    /**
     * Loads today's puzzle with 7-day fallback
     * @returns {Promise<{puzzle: Object, date: string}>}
     */
    static async loadDailyPuzzle() {
        // Check for date override in URL path (e.g., /2026-01-15)
        const pathDate = this.getDateFromPath();

        let today;
        if (pathDate) {
            // Parse as local timezone by using date parts
            const [year, month, day] = pathDate.split('-').map(Number);
            today = new Date(year, month - 1, day); // month is 0-indexed

            // Validate the date is real (e.g., reject 2026-02-30)
            if (isNaN(today.getTime()) ||
                today.getFullYear() !== year ||
                today.getMonth() !== month - 1 ||
                today.getDate() !== day) {
                console.warn(`Invalid date in URL path: ${pathDate}, using today`);
                today = new Date();
            }
        } else {
            today = new Date();
        }

        const todayStr = this.formatDate(today);
        const span = instrumentation?.startSpan('puzzle.load', { puzzleDate: todayStr });

        try {
            for (let daysBack = 0; daysBack < 7; daysBack++) {
                const targetDate = new Date(today);
                targetDate.setDate(today.getDate() - daysBack);
                const dateString = this.formatDate(targetDate);

                try {
                    // Use absolute path so tests work from any directory
                    const response = await fetch(`/puzzles/${dateString}.json`);
                    if (response.ok) {
                        const puzzle = await response.json();

                        // Validate puzzle structure
                        const validation = PuzzleValidator.validate(puzzle);
                        if (!validation.valid) {
                            console.error(`Invalid puzzle format for ${dateString}:`, validation.errors);
                            continue; // Try next day's puzzle
                        }

                        // Track successful puzzle load
                        instrumentation?.trackPuzzleLoaded(dateString, puzzle.categories.length);

                        return { puzzle, date: dateString };
                    }
                } catch (error) {
                    console.warn(`Failed to load puzzle for ${dateString}:`, error);
                    continue;
                }
            }

            // No puzzle available - track error
            const error = new Error('No puzzle available in the last 7 days');
            instrumentation?.trackError(error, {
                operation: 'loadDailyPuzzle',
                puzzleDate: todayStr
            });
            throw error;
        } finally {
            instrumentation?.endSpan(span);
        }
    }

    /**
     * Format date as YYYY-MM-DD
     * @param {Date} date
     * @returns {string}
     */
    static formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Extract date from URL path (e.g., /2026-01-15 -> "2026-01-15")
     * @returns {string|null} Date string if valid, null otherwise
     */
    static getDateFromPath() {
        const path = window.location.pathname;
        // Match /YYYY-MM-DD at the start of the path
        const match = path.match(/^\/(\d{4}-\d{2}-\d{2})(?:\/|$)/);
        return match ? match[1] : null;
    }

    /**
     * Navigate to a specific puzzle date using path-based URL
     * @param {string} dateStr - Date in YYYY-MM-DD format
     */
    static navigateToDate(dateStr) {
        window.location.href = `/${dateStr}`;
    }

    /**
     * Navigate to today's puzzle (root URL)
     */
    static navigateToToday() {
        window.location.href = '/';
    }
}
